import {
  Box,
  Button,
  HStack,
  VStack,
  Checkbox,
  Collapse,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useStores } from "../store/store_context";
import { authFetch } from "../auth/auth";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import baseUrl from "../vars";
import { reorderColumns } from "../utils/report_columns";

// Общая панель "Выбрать колонки" для всех отчётов: чекбоксы видимости +
// перетаскивание строк для смены порядка. Порядок и видимость сохраняются
// в БД персонально для текущего пользователя (report передаётся как ключ).
//
// Компонент не держит собственную копию columns — работает прямо с prop'ом
// (наблюдаемый mobx-массив из стора), чтобы внешние изменения того же массива
// (например, пресет "Компактный вид" в некоторых отчётах) сразу отражались
// здесь без рассинхронизации локального state.
const ColumnManager = observer(({ report, columns, onChange, introducedColumnKeys = [] }) => {
  const { pageStore } = useStores();
  const [isOpen, setIsOpenState] = useState(false);
  const dropdownRef = useRef(null);

  const loadSavedSettings = async () => {
    try {
      const response = await authFetch(
        `${baseUrl}/api/active_columns?report=${report}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${pageStore.token}`,
          },
        }
      );

      const result = await response.json();
      if (!result) return;

      const activeKeys = Array.isArray(result) ? result : result.active || [];
      const savedOrder = Array.isArray(result) ? null : result.order || null;

      const ordered = reorderColumns(columns, savedOrder);
      const knownOrderKeys = savedOrder?.length
        ? new Set(savedOrder.map((key) => String(key)))
        : null;

      const newColumns = ordered.map((col) => {
        const key = String(col.key);
        // Новая колонка (её ещё не было в сохранённом order) — берём active из дефолта
        const isNewColumn = knownOrderKeys
          ? !knownOrderKeys.has(key)
          : introducedColumnKeys.includes(key);

        return {
          ...col,
          active: isNewColumn
            ? col.active
            : (introducedColumnKeys.length > 0 && knownOrderKeys) || activeKeys.length
              ? activeKeys.map(String).includes(key)
              : col.active,
        };
      });

      onChange(newColumns);
    } catch (err) {
      console.error("Error fetching column settings:", err);
    }
  };

  useEffect(() => {
    loadSavedSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const toggleColumn = (index) => {
    const newColumns = [...columns];
    newColumns[index] = { ...newColumns[index], active: !newColumns[index].active };
    onChange(newColumns);
  };

  const selectAllColumns = () => {
    const allActive = columns.every((c) => c.active);
    const newColumns = columns.map((c) => ({ ...c, active: !allActive }));
    onChange(newColumns);
  };

  const handleDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const newColumns = Array.from(columns);
    const [moved] = newColumns.splice(result.source.index, 1);
    newColumns.splice(result.destination.index, 0, moved);

    onChange(newColumns);
  };

  const toggleDropdown = () => setIsOpenState((prev) => !prev);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpenState(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box position="relative" ref={dropdownRef}>
      <Button
        onClick={toggleDropdown}
        rightIcon={isOpen ? <FiChevronUp /> : <FiChevronDown />}
        bg="rgba(51,51,51,1)"
        color="#EEEEEE"
        borderRadius="10px"
        fontSize="14px"
        fontWeight="600"
        minW="400px"
        textAlign="left"
      >
        {columns.filter((c) => c.active).length > 0
          ? `Колонки: ${columns.filter((c) => c.active).length}`
          : "Выбрать колонки"}
      </Button>

      <Collapse in={isOpen} animateOpacity>
        <Box
          position="absolute"
          mt={2}
          bg="#1e1e1e"
          color="#EEEEEE"
          borderRadius="md"
          p={3}
          boxShadow="lg"
          zIndex={1000}
          minW="400px"
          maxH="720px"
          overflowY="auto"
        >
          <Button
            size="sm"
            width="100%"
            mb={2}
            colorScheme="green"
            onClick={selectAllColumns}
          >
            {columns.every((c) => c.active) ? "Снять все" : "Выбрать все"}
          </Button>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="column-manager-list">
              {(provided) => (
                <VStack
                  align="start"
                  spacing={1}
                  width="100%"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {columns.map((col, index) => (
                    <Draggable key={col.key} draggableId={String(col.key)} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <HStack
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          width="100%"
                          bg={dragSnapshot.isDragging ? "#2a2a2a" : "transparent"}
                          borderRadius="md"
                          px={1}
                          spacing={2}
                        >
                          <Box
                            {...dragProvided.dragHandleProps}
                            cursor="grab"
                            color="gray.500"
                            userSelect="none"
                            fontSize="16px"
                            px={1}
                            title="Перетащить"
                          >
                            ⠿
                          </Box>
                          <Checkbox
                            isChecked={col.active}
                            onChange={() => toggleColumn(index)}
                            colorScheme="green"
                            fontSize="14px"
                            width="100%"
                            color="#EEEEEE"
                            sx={{
                              ".chakra-checkbox__label": { color: "#EEEEEE" },
                            }}
                          >
                            {col.title}
                          </Checkbox>
                        </HStack>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </VStack>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </Collapse>
    </Box>
  );
});

export default ColumnManager;
